'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';

// ─── Types ───────────────────────────────────────────────────────────────────
interface FAQItem {
  q: string;
  a: string;
}

// ─── Data ────────────────────────────────────────────────────────────────────
const HIGHLIGHTED_FEATURES = [
  {
    icon: '🎬',
    title: 'YouTube Content ID',
    desc: 'Monetize eligible UGC',
    badge: 'Content ID',
    glow: 'from-red-500/20 to-purple-600/20',
  },
  {
    icon: '🇮🇳',
    title: 'UPI Payouts',
    desc: 'Get paid directly in India',
    badge: 'Direct Bank / UPI',
    glow: 'from-emerald-500/20 to-teal-600/20',
  },
  {
    icon: '🆓',
    title: 'ISRC + UPC',
    desc: 'Included with every release',
    badge: 'Zero Cost Codes',
    glow: 'from-blue-500/20 to-indigo-600/20',
  },
  {
    icon: '💯',
    title: '100% Royalties',
    desc: 'Keep what you earn',
    badge: 'Zero Commission',
    glow: 'from-purple-500/20 to-pink-600/20',
  },
];

const PLATFORMS = [
  { name: 'Spotify', color: '#1DB954' },
  { name: 'Apple Music', color: '#FC3C44' },
  { name: 'YouTube Music', color: '#FF0000' },
  { name: 'Amazon Music', color: '#00A8E1' },
  { name: 'JioSaavn', color: '#2BC5B4' },
  { name: 'Wynk Music', color: '#E4003A' },
  { name: 'TikTok', color: '#010101' },
  { name: 'Instagram Audio', color: '#E1306C' },
  { name: 'Deezer', color: '#A238FF' },
  { name: 'Tidal', color: '#00FFFF' },
  { name: 'Shazam', color: '#0088FF' },
  { name: 'Pandora', color: '#005483' },
  { name: 'Audiomack', color: '#FFA500' },
  { name: 'Gaana', color: '#E72C30' },
  { name: 'Hungama', color: '#FF6600' },
  { name: 'Resso', color: '#FF2D55' },
  { name: 'Tencent Music', color: '#1DB954' },
  { name: 'NetEase Cloud', color: '#C20C0C' },
  { name: 'Anghami', color: '#5B2D8E' },
  { name: 'Boomplay', color: '#FF6B35' },
  { name: 'iHeartRadio', color: '#C6002B' },
  { name: 'Napster', color: '#000000' },
  { name: 'Beatport', color: '#01FF95' },
  { name: 'SoundCloud', color: '#FF5500' },
];

const FAQS: FAQItem[] = [
  {
    q: 'How do I release music through TubeNexa?',
    a: 'Sign up for a free or paid plan, upload your WAV/FLAC audio (16/24-bit) and 3000×3000px cover art, fill in your track metadata, choose your release date, and select your target stores. Our automated QC engine reviews your submission within 24–48 hours before direct delivery.',
  },
  {
    q: 'What is the difference between the Free and Paid plans?',
    a: 'The Free Starter plan allows 5 songs per month with an 85% royalty payout. The Artist Pro (₹1,499/yr) and Label Elite (₹3,499/yr) plans offer unlimited releases, 100% royalties (keep all your revenue), YouTube Content ID, caller tune distribution, and custom label branding.',
  },
  {
    q: 'Do I keep 100% of my music rights and master ownership?',
    a: 'Yes, absolutely. You retain 100% exclusive copyright and ownership over your master sound recordings, lyrics, and publishing. TubeNexa operates solely as your technical distribution infrastructure.',
  },
  {
    q: 'Can I distribute AI-assisted music (Suno / Udio / custom AI)?',
    a: 'Yes — TubeNexa supports AI-assisted tracks provided you own full commercial rights. Simply disclose AI participation during upload to ensure full compliance with DSP policies.',
  },
  {
    q: 'What is the refund policy and support contact?',
    a: 'We provide a 7-day money-back guarantee if a release fails delivery due to technical platform errors. For any assistance, reach out directly to support@tubenexa.com 24/7.',
  },
];

const BLOGS = [
  {
    tag: 'YouTube',
    title: 'How to Claim Your Official Artist Channel on YouTube',
    desc: 'Step-by-step walkthrough to merge your topic channel, get the ♪ music note badge, and unlock OAC features for your artist profile.',
    readTime: '6 min read',
    gradient: 'from-red-900/40 to-purple-900/40',
  },
  {
    tag: 'Distribution',
    title: 'Step-by-Step Guide to Releasing Your First Single in 2026',
    desc: 'From recording to streaming — cover art specs, metadata best practices, ISRC codes, and how to schedule your release for maximum impact.',
    readTime: '8 min read',
    gradient: 'from-violet-900/40 to-indigo-900/40',
  },
  {
    tag: 'Royalties',
    title: 'Understanding Music Royalties: Streaming vs Publishing',
    desc: 'Demystify mechanical royalties, performance rights, sync licensing, and how TubeNexa ensures every stream translates to real earnings in your wallet.',
    readTime: '10 min read',
    gradient: 'from-fuchsia-900/40 to-rose-900/40',
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function Navbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#0a0a0f]/95 backdrop-blur-xl border-b border-[#2a2a3e]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2">
          <img
            src="/assets/images/Brown_Minimalist_YouTube_Banner__3000_x_3000_px_-1787252454715.png"
            alt="TubeNexa Logo"
            className="h-10 sm:h-12 w-auto object-contain max-w-[170px]"
          />
        </Link>
        <div className="hidden md:flex items-center gap-8">
          <a href="#platforms" className="text-sm text-[#6b6b8a] hover:text-white transition-colors">Platforms</a>
          <a href="#features" className="text-sm text-[#6b6b8a] hover:text-white transition-colors">Features</a>
          <a href="#pricing" className="text-sm text-[#6b6b8a] hover:text-white transition-colors">Pricing</a>
          <a href="#faq" className="text-sm text-[#6b6b8a] hover:text-white transition-colors">FAQ</a>
        </div>
        <div className="flex items-center gap-3">
          <Link
            href="/sign-up-login-screen"
            className="text-sm text-[#c4c4d4] hover:text-white transition-colors px-4 py-2"
          >
            Sign In
          </Link>
          <Link
            href="/sign-up-login-screen"
            className="text-sm font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white px-5 py-2 rounded-lg hover:opacity-90 transition-opacity"
          >
            Distribute Music
          </Link>
        </div>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-24 pb-12">
      {/* Animated background */}
      <div className="absolute inset-0 bg-[#0a0a0f]">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-600/20 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-fuchsia-500/15 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-violet-900/10 rounded-full blur-[150px]" />
      </div>

      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(#a78bfa 1px, transparent 1px), linear-gradient(90deg, #a78bfa 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-violet-600/10 border border-violet-500/20 rounded-full px-4 py-1.5 mb-8">
          <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-pulse" />
          <span className="text-xs text-violet-300 font-medium tracking-wide uppercase">Now live — 150+ Streaming Platforms</span>
        </div>

        <h1 className="text-5xl md:text-7xl font-bold text-white leading-[1.05] mb-6 tracking-tight">
          Distribute Your Music{' '}
          <span
            className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400 bg-clip-text text-transparent"
          >
            Globally
          </span>{' '}
          to 150+ Streaming Platforms
        </h1>

        <p className="text-lg md:text-xl text-[#6b6b8a] max-w-2xl mx-auto mb-10 leading-relaxed">
          Keep up to 100% of your earnings, get free ISRC/UPC codes, YouTube Content ID, and fast distribution to Spotify, Apple Music, JioSaavn, Wynk, and beyond.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
          <Link
            href="/sign-up-login-screen"
            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white font-semibold px-8 py-4 rounded-xl text-base hover:opacity-90 transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(124,58,237,0.4)]"
          >
            🚀 Distribute Your Music
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
          <a
            href="#pricing"
            className="inline-flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-white font-semibold px-8 py-4 rounded-xl text-base hover:bg-white/10 transition-all"
          >
            Explore Plans
          </a>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap justify-center gap-6">
          {[
            { icon: '⚡', label: 'Fast 24–48h Delivery' },
            { icon: '🚫', label: 'Zero Hidden Fees' },
            { icon: '💰', label: 'Up to 100% Royalties' },
            { icon: '🎵', label: 'Free ISRC & UPC Codes' },
          ].map((badge) => (
            <div key={badge.label} className="flex items-center gap-2 text-sm text-[#6b6b8a]">
              <span>{badge.icon}</span>
              <span>{badge.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── NEW HIGHLIGHTED VALUE PROPOSITIONS SECTION ─────────────────────────────
function HighlightedPropsSection() {
  return (
    <section className="py-6 px-6 max-w-7xl mx-auto -mt-6 mb-12 relative z-20">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {HIGHLIGHTED_FEATURES.map((item, idx) => (
          <div
            key={idx}
            className="group relative p-5 rounded-2xl bg-[#13131a]/90 border border-[#2a2a3e] hover:border-violet-500/60 transition-all duration-300 hover:-translate-y-1.5 shadow-xl overflow-hidden backdrop-blur-md"
          >
            <div
              className={`absolute inset-0 bg-gradient-to-br ${item.glow} opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`}
            />

            <div className="relative z-10 space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-2xl drop-shadow-md">{item.icon}</span>
                <span className="text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full bg-violet-950/80 border border-violet-700/50 text-violet-300">
                  {item.badge}
                </span>
              </div>

              <div>
                <h3 className="text-sm font-bold text-white group-hover:text-violet-300 transition-colors">
                  {item.title}
                </h3>
                <p className="text-xs text-[#6b6b8a] mt-0.5 font-medium leading-relaxed">
                  {item.desc}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function PlatformsSection() {
  const row1 = PLATFORMS.slice(0, 12);
  const row2 = PLATFORMS.slice(12);

  return (
    <section id="platforms" className="py-24 overflow-hidden bg-[#0a0a0f]">
      <div className="max-w-7xl mx-auto px-6 mb-12 text-center">
        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#6b6b8a] mb-3">Distribution Network</p>
        <h2 className="text-3xl md:text-4xl font-bold text-white">
          Your music on every platform{' '}
          <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
            that matters
          </span>
        </h2>
      </div>

      {/* Marquee row 1 — left */}
      <div className="relative mb-4">
        <div className="flex gap-4 animate-[marquee_30s_linear_infinite]" style={{ width: 'max-content' }}>
          {[...row1, ...row1].map((p, i) => (
            <PlatformPill key={`r1-${i}`} name={p.name} color={p.color} />
          ))}
        </div>
      </div>

      {/* Marquee row 2 — right */}
      <div className="relative">
        <div className="flex gap-4 animate-[marquee_25s_linear_infinite_reverse]" style={{ width: 'max-content' }}>
          {[...row2, ...row2, ...row2].map((p, i) => (
            <PlatformPill key={`r2-${i}`} name={p.name} color={p.color} />
          ))}
        </div>
      </div>

      <div className="text-center mt-10">
        <span className="text-sm text-[#6b6b8a]">+ 130 more platforms worldwide including regional stores across Asia, Africa & Latin America</span>
      </div>

      <style jsx>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}

function PlatformPill({ name, color }: { name: string; color: string }) {
  return (
    <div className="flex items-center gap-2.5 bg-[#13131a] border border-[#2a2a3e] rounded-full px-5 py-2.5 whitespace-nowrap hover:border-violet-500/40 transition-colors shadow-sm">
      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
      <span className="text-sm text-[#c4c4d4] font-medium">{name}</span>
    </div>
  );
}

function FeaturesSection() {
  const features = [
    {
      icon: '🎬',
      title: 'YouTube Content ID & Audio Fingerprinting',
      desc: 'Register your tracks for automatic Content ID scanning. Monetise, track, or block any video using your music — across all of YouTube.',
      span: 'col-span-1 md:col-span-2',
      bg: 'from-red-900/20 to-violet-900/20',
    },
    {
      icon: '♪',
      title: 'Official Artist Channel (OAC)',
      desc: 'Get the music note badge and merge your topic channel with your official YouTube presence.',
      span: 'col-span-1',
      bg: 'from-violet-900/20 to-fuchsia-900/20',
    },
    {
      icon: '🤝',
      title: 'Automated Royalty Splits',
      desc: 'Set percentage splits for collaborators, producers, and co-writers. Payouts are calculated and distributed automatically every month.',
      span: 'col-span-1',
      bg: 'from-fuchsia-900/20 to-pink-900/20',
    },
    {
      icon: '📊',
      title: 'Real-Time Analytics & Direct Payouts',
      desc: 'Track streams, revenue, and audience demographics across all DSPs in one dashboard. Withdraw via UPI or Direct Bank Transfer.',
      span: 'col-span-1 md:col-span-2',
      bg: 'from-indigo-900/20 to-violet-900/20',
    },
    {
      icon: '✅',
      title: 'Zero-Rejection QC Engine',
      desc: 'Our fast-track quality control catches metadata errors, audio issues, and artwork problems before submission — so your release never gets rejected.',
      span: 'col-span-1 md:col-span-3',
      bg: 'from-emerald-900/20 to-teal-900/20',
    },
  ];

  return (
    <section id="features" className="py-24 bg-[#0a0a0f]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-14 max-w-xl">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#6b6b8a] mb-3">Platform Features</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
            Everything an independent artist needs to{' '}
            <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
              go pro
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {features.map((f) => (
            <div
              key={f.title}
              className={`${f.span} relative rounded-2xl border border-[#2a2a3e] p-7 overflow-hidden group hover:border-violet-500/40 transition-all duration-300`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${f.bg} opacity-60`} />
              <div className="relative z-10">
                <span className="text-3xl mb-4 block">{f.icon}</span>
                <h3 className="text-lg font-bold text-white mb-2">{f.title}</h3>
                <p className="text-sm text-[#6b6b8a] leading-relaxed">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  const plans = [
    {
      name: 'Free Starter',
      price: '₹0',
      period: 'forever',
      royalty: '85%',
      highlight: false,
      color: 'border-[#2a2a3e]',
      badge: 'Zero Upfront Cost',
      features: [
        '5 releases per month',
        '1 Artist Profile (Single Artist)',
        '150+ streaming stores (Spotify, JioSaavn, Wynk, Apple)',
        'Free Official ISRC & UPC codes',
        '85% royalty payout share',
        'Standard 3-5 days Quality Control review',
        'Basic streaming analytics',
        'Email support',
      ],
    },
    {
      name: 'Artist Pro',
      price: '₹1,499',
      period: 'per year',
      royalty: '100%',
      highlight: true,
      color: 'border-violet-500/60',
      badge: 'Most Popular',
      features: [
        'Single Artist Account (1 Profile)',
        'Unlimited releases (Singles, EPs, Albums)',
        '100% royalty — keep all your earnings',
        'Priority Fast-Track QC (24-48h approval)',
        'YouTube Content ID monetization',
        'Official Artist Channel (OAC) verification',
        'Synced lyrics & Caller Tune distribution',
        'Real-time streaming analytics',
        'UPI / Direct Bank Transfer payouts',
      ],
    },
    {
      name: 'Label Elite',
      price: '₹3,499',
      period: 'per year',
      royalty: '100%',
      highlight: false,
      color: 'border-fuchsia-500/40',
      badge: 'For Labels & Studios',
      features: [
        'Manage up to 4 Artists / Sub-Profiles',
        'Unlimited releases across all artists',
        '100% royalty for all artists (0% platform cut)',
        'Custom Record Label branding on DSPs',
        'YouTube Content ID for all artist tracks',
        'Automated multi-artist royalty split accounting',
        'Bulk ISRC and metadata management',
        'VIP Priority same-day QC review',
        'Dedicated 24/7 account manager',
      ],
    },
  ];

  return (
    <section id="pricing" className="py-24 bg-[#0a0a0f]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#6b6b8a] mb-3">Pricing</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-[#6b6b8a] mb-6">Start free or upgrade to keep 100% royalties. No hidden cuts. Cancel anytime.</p>
          <div className="inline-flex items-center bg-[#13131a] border border-[#2a2a3e] rounded-lg px-4 py-2">
            <span className="text-sm font-semibold text-violet-400">₹ INR — Indian Rupee</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl border ${plan.color} p-8 flex flex-col ${
                plan.highlight ? 'bg-gradient-to-b from-violet-900/20 to-[#13131a] shadow-[0_0_40px_rgba(124,58,237,0.15)]' : 'bg-[#13131a]'
              }`}
            >
              {plan.badge && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold px-4 py-1 rounded-full ${
                  plan.highlight ? 'bg-violet-600 text-white' : 'bg-fuchsia-600 text-white'
                }`}>
                  {plan.badge}
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-lg font-bold text-white mb-1">{plan.name}</h3>
                <div className="flex items-end gap-1 mb-1">
                  <span className="text-4xl font-bold text-white">
                    {plan.price}
                  </span>
                  <span className="text-[#6b6b8a] text-sm mb-1">/{plan.period}</span>
                </div>
                <span className="text-xs text-violet-400 font-semibold">{plan.royalty} Royalties Payout</span>
              </div>

              <ul className="space-y-3 flex-1 mb-8">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-[#c4c4d4]">
                    <svg className="w-4 h-4 text-violet-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {f}
                  </li>
                ))}
              </ul>

              <Link
                href="/sign-up-login-screen"
                className={`w-full text-center py-3 rounded-xl font-semibold text-sm transition-all ${
                  plan.highlight
                    ? 'bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white hover:opacity-90 hover:shadow-[0_0_20px_rgba(124,58,237,0.3)]'
                    : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'
                }`}
              >
                {plan.name === 'Free Starter' ? 'Start for Free' : 'Get Started →'}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <section id="faq" className="py-24 bg-[#0a0a0f]">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#6b6b8a] mb-3">FAQ</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white">Common questions</h2>
        </div>

        <div className="space-y-3">
          {FAQS.map((faq, i) => (
            <div
              key={i}
              className="border border-[#2a2a3e] rounded-xl overflow-hidden hover:border-violet-500/30 transition-colors"
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left bg-[#13131a] hover:bg-[#1a1a24] transition-colors"
              >
                <span className="text-sm font-semibold text-white">{faq.q}</span>
                <svg
                  className={`w-5 h-5 text-violet-400 flex-shrink-0 transition-transform duration-200 ${open === i ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {open === i && (
                <div className="px-6 py-5 bg-[#0f0f16] border-t border-[#2a2a3e]">
                  <p className="text-sm text-[#6b6b8a] leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BlogSection() {
  return (
    <section className="py-24 bg-[#0a0a0f]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#6b6b8a] mb-3">Creator Guides</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Learn from the{' '}
              <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
                music industry
              </span>
            </h2>
          </div>
          <Link href="/sign-up-login-screen" className="hidden md:inline-flex text-sm text-violet-400 hover:text-violet-300 transition-colors items-center gap-1">
            View all guides
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {BLOGS.map((blog) => (
            <div
              key={blog.title}
              className="relative rounded-2xl border border-[#2a2a3e] overflow-hidden group cursor-pointer hover:border-violet-500/40 transition-all duration-300 hover:-translate-y-1"
            >
              <div className={`h-36 bg-gradient-to-br ${blog.gradient} flex items-center justify-center`}>
                <span className="text-4xl opacity-40">♪</span>
              </div>
              <div className="p-6 bg-[#13131a]">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-violet-400 bg-violet-600/10 border border-violet-500/20 px-3 py-1 rounded-full">
                    {blog.tag}
                  </span>
                  <span className="text-xs text-[#6b6b8a]">{blog.readTime}</span>
                </div>
                <h3 className="text-base font-bold text-white mb-2 leading-snug group-hover:text-violet-300 transition-colors">
                  {blog.title}
                </h3>
                <p className="text-sm text-[#6b6b8a] leading-relaxed">{blog.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTABanner() {
  return (
    <section className="py-20 bg-[#0a0a0f]">
      <div className="max-w-4xl mx-auto px-6">
        <div className="relative rounded-3xl overflow-hidden border border-violet-500/20 p-12 text-center">
          <div className="absolute inset-0 bg-gradient-to-br from-violet-900/30 via-fuchsia-900/20 to-[#0a0a0f]" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-violet-600/20 rounded-full blur-[80px]" />
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Your music deserves to be heard everywhere
            </h2>
            <p className="text-[#6b6b8a] mb-8 max-w-xl mx-auto">
              Join thousands of independent artists already distributing with TubeNexa. Start free — no credit card required.
            </p>
            <Link
              href="/sign-up-login-screen"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white font-semibold px-8 py-4 rounded-xl text-base hover:opacity-90 transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(124,58,237,0.4)]"
            >
              Get Started Free — It&apos;s Free Forever
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0a0a0f] border-t border-[#2a2a3e] py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-start justify-between gap-8 mb-10">
          <div className="max-w-xs">
            <div className="flex items-center mb-3">
              <img
                src="/assets/images/Brown_Minimalist_YouTube_Banner__3000_x_3000_px_-1787252454715.png"
                alt="TubeNexa Logo"
                className="h-10 w-auto object-contain max-w-[150px]"
              />
            </div>
            <p className="text-xs text-[#6b6b8a] leading-relaxed mb-2">
              Independent music distribution to 150+ global streaming platforms. Keep up to 100% of your royalties.
            </p>
            <p className="text-xs text-[#6b6b8a]">
              Support: <a href="mailto:support@tubenexa.com" className="text-violet-400 hover:underline">support@tubenexa.com</a>
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 text-sm">
            <div>
              <p className="text-white font-semibold mb-3">Platform</p>
              <ul className="space-y-2">
                <li><a href="#features" className="text-[#6b6b8a] hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="text-[#6b6b8a] hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#platforms" className="text-[#6b6b8a] hover:text-white transition-colors">Platforms</a></li>
                <li><Link href="/sign-up-login-screen" className="text-[#6b6b8a] hover:text-white transition-colors">Sign Up</Link></li>
              </ul>
            </div>
            <div>
              <p className="text-white font-semibold mb-3">Support</p>
              <ul className="space-y-2">
                <li><Link href="/support" className="text-[#6b6b8a] hover:text-white transition-colors">24/7 Support Center</Link></li>
                <li><a href="#faq" className="text-[#6b6b8a] hover:text-white transition-colors">FAQ</a></li>
                <li><a href="mailto:support@tubenexa.com" className="text-[#6b6b8a] hover:text-white transition-colors">Email Us</a></li>
              </ul>
            </div>
            <div>
              <p className="text-white font-semibold mb-3">Legal</p>
              <ul className="space-y-2">
                <li><Link href="/legal-documents" className="text-[#6b6b8a] hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link href="/legal-documents" className="text-[#6b6b8a] hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link href="/legal-documents" className="text-[#6b6b8a] hover:text-white transition-colors">Distribution Agreement</Link></li>
                <li><Link href="/legal-documents" className="text-[#6b6b8a] hover:text-white transition-colors">Content Guidelines & AI Policy</Link></li>
                <li><Link href="/legal-documents" className="text-[#6b6b8a] hover:text-white transition-colors">Refund Policy (7 Days)</Link></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-[#2a2a3e] pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs text-[#6b6b8a]">© 2026 TubeNexa. All rights reserved.</p>
          <p className="text-xs text-[#6b6b8a]">Made for independent artists worldwide 🎵</p>
        </div>
      </div>
    </footer>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Navbar />
      <HeroSection />
      <HighlightedPropsSection />
      <PlatformsSection />
      <FeaturesSection />
      <PricingSection />
      <FAQSection />
      <BlogSection />
      <CTABanner />
      <Footer />
    </div>
  );
}